import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all, fetch_all_with_count, fetch_one } from "../../lib/v31lib";
import { loginAtom } from '../../lib/auth';
import { currentPracticeAtom } from '../../lib/practice';

import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BookIcon from '@mui/icons-material/Book';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import EuroIcon from '@mui/icons-material/Euro';
import SchoolIcon from '@mui/icons-material/School';
import TvIcon from '@mui/icons-material/Tv';

import Logo from '../../images/logo_nutriportal2024.png';

import {
  Avatar,
  Box,
  Button as Btn,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContentText,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
} from '@mui/material';

import {
  Button,
  DatePicker,
  Dt,
  Page,
  Select,
  TextInput,
  Typography
} from '../../components/v2/styled';
import { Base as DialogBase } from '../../components/v2/dialogs/Base';

import { KnowledgeCourse } from '../../models/KnowledgeCourse';

import HelpersLanguage from '../../actions/helpers/language';
import { CardBase } from '../../components/v2/cards/Base';
import { StyledKnowledgeSubject } from '../../components/v2/styled/knowledge/Subject';
import { User } from '../../models/User';
import { Country } from '../../models/Country';
import Person from '@mui/icons-material/Person';
import PersonAddAlt1 from '@mui/icons-material/PersonAddAlt1';
import Email from '@mui/icons-material/Email';
import Flag from '@mui/icons-material/Flag';
import PhoneAndroid from '@mui/icons-material/PhoneAndroid';
import { CoachSkill } from '../../models/CoachSkill';
import Done from '@mui/icons-material/Done';
import Add from '@mui/icons-material/Add';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PointOfSale from '@mui/icons-material/PointOfSale';

const { DateTime } = require("luxon");

type Props = {}

export const SharedKnowledgeDetail: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  const params = useParams();

  const [objectId, setObjectId] = useState<string | undefined>(params.id);
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>("created_at DESC");
  const [search, setSearch] = useState<string>("");
  const [videoActiveLanguage, setVideoActiveLanguage] = useState<string>(i18n.resolvedLanguage || 'nl');
  const [registerLogin, setRegisterLogin] = useState<boolean>(false);
  const [validatedUser, setValidatedUser] = useState<User>();
  const [validUser, setValidUser] = useState<boolean>(false);
  const [newUser, setNewUser] = useState<User>();

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [isLoginError, setIsLoginError] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<{
    success: boolean;
    data: any;
  }>();
  const [errors, setErrors] = useState<{[z:string]: boolean}>({});
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [birthDate, setBirthDate] = useState<string>('');
  const [passwordRepeat, setPasswordRepeat] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [mobile, setMobile] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [emailExists, setEmailExists] = useState<boolean>(false);
  const [gdprAccepted, setGdprAccepted] = useState<boolean>(false);
  const [practiceGdprAccepted, setPracticeGdprAccepted] = useState<boolean>(false);
  const [showPracticeGdpr, setShowPracticeGdpr] = useState<boolean>(false);
  const [isGuest, setIsGuest] = useState<boolean>(false);
  const [isRestricted, setIsRestricted] = useState<boolean>(false);
  const [hasChosen, setHasChosen] = useState<boolean>(false);
  const [practiceName, setPracticeName] = useState<string>('');
  const [practiceEmail, setPracticeEmail] = useState<string>('');
  const [skillIds, setSkillIds] = useState<number[]>([]);
  const [showAllChips, setShowAllChips] = useState<boolean>(false);

  const [forgotPasswordOpen, setForgotPasswordOpen] = useState<boolean>(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState<boolean>(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState<boolean>(false);

  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const {
    data: object,
    isLoading: objectLoading,
    isError: objectError,
    isSuccess: objectSuccess,
    refetch: objectRefetch,
  } = useQuery({
    queryKey: ["coach", "knowledge_course", objectId, {page: page, order: order, search: search}],
    queryFn: () =>
      fetch_one<KnowledgeCourse>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/fetch_all`,
        {
          object: 'knowledge_course',
          fields: [
            'id', 'full_name', 'full_teaser', 'full_body', 'extra_data', 'get_item', 'is_bg_color', 'practice', 
            'knowledge_subjects', 'knowledge_lessons', 'access_data', 'mollie_data', 'is_nutri', 'is_it',
            'is_approvable', 'course_type'
          ],
          sub_fields: {
            practice: ['id', 'name', 'get_item', 'get_medium'],
            knowledge_subjects: ['id', 'full_name', 'full_body', 'knowledge_lessons'],
            knowledge_lessons: ['id', 'full_name', 'videos', 'attachments', 'is_video_lesson'],
            knowsub: ['id', 'created_at', 'lesson_key', 'is_approved', 'mollie_link'],
            mollie_link: ['id', 'paid', 'extra_data']
          },
          id: objectId
        },
        undefined
      ),
    enabled: !!objectId,
  });
  // const {
  //   data: coachSkills,
  //   isLoading: coachSkillsLoading,
  //   isError: coachSkillsError,
  //   isSuccess: coachSkillsSuccess,
  //   // refetch: coachesRefetch,
  // } = useQuery({
  //   queryKey: ["coach_skills"],
  //   queryFn: () =>
  //     fetch_all<CoachSkill>(
  //       `/${i18n.resolvedLanguage || 'nl'}/v3/pub/fetch_all`,
  //       {
  //         object: 'coach_skill',
  //         fields: ['id', 'name'],
  //         order: 'name ASC'
  //       }
  //     ),
  //   enabled: true
  // });
  const {
    data: coachSkills,
    isLoading: coachSkillsLoading,
    isError: coachSkillsError,
    isSuccess: coachSkillsSuccess,
    refetch: coachSkillsRefetch,
  } = useQuery({
    queryKey: ["coach_skills", i18n.resolvedLanguage],
    queryFn: () =>
      fetch_all<CoachSkill>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/fetch_all`,
        {
          object: 'coach_skill',
          fields: ['id', 'name', 'name_en', 'name_fr', 'name_de'],
          order: 'name ASC'
        },
        undefined
      ),
    select: (res) => {
      let tempRes:CoachSkill[] = [];
      res.map(rrr => {
        let rrrClone = {...rrr};
        if (!!rrrClone.name_en && i18n.resolvedLanguage === 'en') rrrClone.name = rrrClone.name_en;
        if (!!rrrClone.name_fr && i18n.resolvedLanguage === 'fr') rrrClone.name = rrrClone.name_fr;
        if (!!rrrClone.name_de && i18n.resolvedLanguage === 'de') rrrClone.name = rrrClone.name_de;

        tempRes.push(rrrClone);
      });
      return tempRes
    },
    enabled: true
  });
  const {
    data: countries,
    isLoading: countriesLoading,
    isError: objectsError,
    isSuccess: objectsSuccess,
    // refetch: obectsRefetch,
  } = useQuery({
    queryKey: ["countries", "select_list", i18n.resolvedLanguage],
    queryFn: () =>
      fetch_all<Country>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/fetch_all`,
        {
          object: 'country',
          fields: ['id', 'name', 'iso', 'name_en', 'name_de', 'name_fr'],
          per_page: 20,
          page: 0,
          order: "name ASC"
        }
      ),
    select: (res) => {
      let tempRes:Country[] = [];
      res.map(rrr => {
        let rrrClone = {...rrr};
        if (!!rrrClone.name_en && i18n.resolvedLanguage === 'en') rrrClone.name = rrrClone.name_en;
        if (!!rrrClone.name_fr && i18n.resolvedLanguage === 'fr') rrrClone.name = rrrClone.name_fr;
        if (!!rrrClone.name_de && i18n.resolvedLanguage === 'de') rrrClone.name = rrrClone.name_de;

        tempRes.push(rrrClone);
      });
      return tempRes
    },
    enabled: true,
  });







  const mutationCustomAction = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{custom_result: {success?: boolean;}}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/custom_action`,
        pars.formData,
        undefined
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      // navigate('/login', {state: {show_knowledge_success: true}});
      setShowSuccess(true);
    },
  });
  const mutationCheckEmail = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{exists: boolean}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/pub/check_email`,
        pars.formData,
        undefined
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      // setIsLoginError(false);
      // setLoginError(undefined);
      return true;
    },
    onError: (data, variables, context) => {
      // setIsLoginError(true);
      // setLoginError(data as any);
      return true;
    },
    onSuccess: (data, variables, context) => {
      setEmailExists(data.exists);
    },
  });
  const mutationForgotPassword = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<User>(
        `/${i18n.resolvedLanguage || 'nl'}/users/forgot_password`,
        pars.formData,
        undefined
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      setForgotPasswordSuccess(false);
      setForgotPasswordLoading(true);
      return true;
    },
    onError: (data, variables, context) => {
      setForgotPasswordLoading(false);
      return true;
    },
    onSuccess: (data, variables, context) => {
      setForgotPasswordSuccess(true);
      setForgotPasswordLoading(false);
    },
  });
  const mutationLogin = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<User>(
        `/${i18n.resolvedLanguage || 'nl'}/users/login`,
        pars.formData,
        undefined
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      setIsLoginError(false);
      setLoginError(undefined);
      return true;
    },
    onError: (data, variables, context) => {
      setIsLoginError(true);
      setLoginError(data as any);
      return true;
    },
    onSuccess: (data, variables, context) => {
      // setLogin(data);
      // set_login(data.email, data.auth_token);
      // navigate('/');
      setValidatedUser(data);
    },
  });









  const selectContentWithId = (field?:{[z:string]: string}, extraFallback?:string) => {
    let correct = (field || {})[HelpersLanguage.mapping_to_id(i18n.resolvedLanguage || 'nl')];
    if (!!correct) return correct;
    let fallback = (field || {})['nl'];
    if (!!fallback) return fallback;
    let anyFallback = Object.values(field || {}).filter(fff => !!fff)[0];
    if (!!anyFallback) return anyFallback;
    if (!!extraFallback) return extraFallback;
    return t("general.translation_fallbacks.no_translation_available")
  }

  useEffect(() => {
    if (params.id !== 'new') objectRefetch();
  }, [params.id]);
  useEffect(() => {
    let to = setTimeout(() => {
      if (!showLogin && !!email) {
        // check email existence
        mutationCheckEmail.mutate({formData: {email: email}});
      }
    }, 1000);

    return () => {
      clearTimeout(to);
    }
  }, [email]);
  useEffect(() => {
    if (!showLogin) {
      if (
        !!firstName && 
        !!lastName && 
        !!email && !!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/.test(email) &&
        !emailExists && 
        !!birthDate && 
        !!country && 
        mobile && 
        (!!isGuest || (!isGuest && !!password && password.length > 7 && password === passwordRepeat)) && 
        !!gdprAccepted && 
        (!!isRestricted ? (
          !!practiceName && !!practiceEmail && !!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/.test(practiceEmail)
        ) : (
          !currentPractice?.get_description || (!!currentPractice?.get_description && !!practiceGdprAccepted)
        ))
      ) {
        setErrors({});
        setValidUser(true);
        setNewUser({
          first_name: firstName,
          last_name: lastName,
          email: email,
          country_id: parseInt(country, 10),
          birth_date: birthDate,
          phone_mobile: mobile,
          password: password,
          password_repeat: passwordRepeat,
          is_agreed: !!gdprAccepted,
          is_agreed_practice: (!!currentPractice?.get_description && !!practiceGdprAccepted),
          practice_id: currentPractice?.id,
          auth_token: '',
          coach_skills: [],
          extra_data: {
            is_guest: isGuest
          }
        });
      } else {
        setErrors({
          first_name: !firstName,
          last_name: !lastName,
          email: !email || !!emailExists || !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/.test(email),
          country: !country,
          birth_date: !birthDate,
          phone_mobile: !mobile,
          password: !password || password.length < 8,
          password_repeat: password !== passwordRepeat,
          gdpr: !gdprAccepted,
          gdpr_practice: (!!currentPractice?.get_description && !practiceGdprAccepted),
          practice_name: !practiceName,
          support_email: !practiceEmail || !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/.test(practiceEmail),
        });
        setValidUser(false);
        setNewUser(undefined);
      }
    } else {
      setValidUser(false);
      setNewUser(undefined);
    }
  }, [showLogin, firstName, lastName, email, emailExists, birthDate, country, mobile, isGuest, password, passwordRepeat, gdprAccepted, practiceGdprAccepted, practiceName, practiceEmail]);

  return <Page
    title={selectContentWithId(object?.full_name || {})}
    titleCrumbs={[
      {
        title: t("coach.menu.knowledge_center"),
        path: "/knowledge_center"
      }
    ]}
  >
    <Grid container spacing={1}>
      {!registerLogin && !showSuccess && <Grid item xs={12} md={8}>
{/* video */}
        {Object.values(object?.extra_data?.video_url || {}).filter(fff => !!fff).length > 0 && <CardBase sx={{height: 'auto', marginBottom: 2}}>
          <CardContent>
            <video id="video" controls style={{objectFit: 'contain', width: '100%'}}>
              <source src={`${(object?.extra_data?.video_url || {})[videoActiveLanguage]}?${videoActiveLanguage}`} type="video/mp4"/>
            </video>
            <Typography sx={{
              textDecoration: 'underline'
            }}>{t("client.course.subtitles.video_available_in")}</Typography>
            <Box sx={{
              height: '40px',
              display: 'flex',
              flexDirection: 'row'
            }}>
              {Object.keys(object?.extra_data?.video_url || {}).filter(fff => !!(object?.extra_data?.video_url || {})[fff]).map(l => <Avatar
                sx={{
                  backgroundColor: l === videoActiveLanguage ? theme.palette.primary.main : 'white',
                  color: l === videoActiveLanguage ? 'white' : theme.palette.primary.main,
                  border: '1px solid white',
                  borderColor: l === videoActiveLanguage ? 'white' : theme.palette.primary.main,
                  marginRight: 1,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setVideoActiveLanguage(l);
                  var video = document.getElementById('video');
                  // @ts-ignore
                  video.load();
                }}
              >
                {l}
              </Avatar>)}
            </Box>
          </CardContent>
        </CardBase>}

        <CardBase sx={{height: 'auto'}}>
          <CardContent>
            <Typography sx={{
              color: theme.palette.tertiary?.main,
              marginBottom: 1,
              fontWeight: 'bold'
            }}>{t("client.course_readmore.titles.summary")}</Typography>
            <Divider />

            <Typography html clean>{selectContentWithId(object?.full_body || {})}</Typography>
          </CardContent>
        </CardBase>

        <CardBase sx={{height: 'auto', marginTop: 2}}>
          <CardContent>
            <Typography sx={{
              color: theme.palette.tertiary?.main,
              marginBottom: 1,
              fontWeight: 'bold'
            }}>{t("client.course_readmore.titles.chapters")}</Typography>
            <Divider sx={{marginBottom: 2}} />

            {!!object && (object?.knowledge_subjects || []).map((subject, i) => <>
              <StyledKnowledgeSubject
                knowledgeCourse={object}
                knowledgeSubject={subject}
                i={i}
                collapseLessons
              />
            </>)}
          </CardContent>
        </CardBase>
      </Grid>}

      {!registerLogin && !showSuccess && <Grid item xs={12} md={4}>
        <CardBase sx={{height: 'auto'}}>
          <CardContent sx={{paddingBottom: "16px !important"}}>
            {!object?.knowsub?.id && <Button
              label={t("client.course_readmore.cta_subscribe")}
              id="sub_courses"
              contained
              onClick={() => {
                setRegisterLogin(true);
              }}
              sx={{
                marginTop: 0
              }}
              fullwidth
            />}
          </CardContent>
        </CardBase>
        <CardBase sx={{height: 'auto', marginTop: 2}}>
          <CardContent>
            <Typography sx={{
              color: theme.palette.tertiary?.main,
              marginBottom: 1,
              fontWeight: 'bold'
            }}>{t("client.course_readmore.titles.made_by")}</Typography>
            <Divider />

            <List sx={{
              "& li": {
                paddingBottom: 0.5,
                paddingTop: 0.5
              }
            }}>
              {!!object?.is_nutri && <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{
                    backgroundColor: 'white',
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    "& img": {
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }
                  }} src={Logo} alt={t("general.terms.nutriportal")}></Avatar>
                </ListItemAvatar>
                <ListItemText primary={t("general.terms.nutriportal")} />
              </ListItem>}
              {!!object?.is_it && <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{
                    backgroundColor: 'white',
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    "& img": {
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }
                  }} src={Logo} alt={t("general.terms.it_team")}></Avatar>
                </ListItemAvatar>
                <ListItemText primary={t("general.terms.it_team")} />
              </ListItem>}
              {!object?.is_nutri && !object?.is_it && <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{
                    backgroundColor: 'white',
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    "& img": {
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }
                  }} src={object?.practice?.get_medium} alt={object?.practice?.name}></Avatar>
                </ListItemAvatar>
                <ListItemText primary={object?.practice?.name} />
              </ListItem>}
            </List>
            <Typography sx={{
              color: theme.palette.tertiary?.main,
              marginBottom: 1,
              fontWeight: 'bold'
            }}>{t(object?.course_type === 1 ? "client.course_readmore.knowledge_titles.about" : "client.course_readmore.titles.about")}</Typography>
            <Divider />


            <List sx={{
              "& li": {
                paddingBottom: 0.5,
                paddingTop: 0.5
              }
            }}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{
                    backgroundColor: 'white',
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    "& img": {
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }
                  }}>
                    <BookIcon color="primary" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={`${(object?.knowledge_subjects?.length || 0)} ${t(`client.course_readmore.stats.chapters_${(object?.knowledge_subjects?.length || 0) === 1 ? 'single' : 'multi'}`)}`} />
              </ListItem>
              {((object?.knowledge_lessons || []).filter(fff => !!fff.is_video_lesson).length || 0) > 0 && <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{
                    backgroundColor: 'white',
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    "& img": {
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }
                  }}>
                    <TvIcon color="primary" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={`${((object?.knowledge_lessons || []).filter(fff => !!fff.is_video_lesson).length || 0)} ${t(`client.course_readmore.${object?.course_type === 1 ? "knowledge_" : ""}stats.lesson_video_${((object?.knowledge_lessons || []).filter(fff => !!fff.is_video_lesson).length || 0) === 1 ? 'single' : 'multi'}`)}`} />
              </ListItem>}
              {((object?.knowledge_lessons || []).filter(fff => !fff.is_video_lesson).length || 0) > 0 && <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{
                    backgroundColor: 'white',
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    "& img": {
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }
                  }}>
                    <SchoolIcon color="primary" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={`${((object?.knowledge_lessons || []).filter(fff => !fff.is_video_lesson).length || 0)} ${t(`client.course_readmore.${object?.course_type === 1 ? "knowledge_" : ""}stats.lesson_text_${((object?.knowledge_lessons || []).filter(fff => !fff.is_video_lesson).length || 0) === 1 ? 'single' : 'multi'}`)}`} />
              </ListItem>}
              {(object?.knowledge_lessons || []).reduce((acc, cur) => acc + Object.values(cur.attachments || {}).reduce((aacc, ccur) => aacc + (ccur || []).length, 0), 0) > 0 && <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{
                    backgroundColor: 'white',
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    "& img": {
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }
                  }}>
                    <AttachFileIcon color="primary" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={`${(object?.knowledge_lessons || []).reduce((acc, cur) => acc + Object.values(cur.attachments || {}).reduce((aacc, ccur) => aacc + (ccur || []).length, 0), 0)} ${t(`client.course_readmore.stats.attachment_${(object?.knowledge_lessons || []).reduce((acc, cur) => acc + Object.values(cur.attachments || {}).reduce((aacc, ccur) => aacc + (ccur || []).length, 0), 0) === 1 ? 'single' : 'multi'}`)}`} />
              </ListItem>}
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{
                    backgroundColor: 'white',
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    "& img": {
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }
                  }}>
                    {!!object?.mollie_data?.cost && parseFloat(object?.mollie_data?.cost) > 0 && <EuroIcon color="primary" />}
                    {!(!!object?.mollie_data?.cost && parseFloat(object?.mollie_data?.cost) > 0) && <CreditCardOffIcon color="primary" />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={`${t(`client.course_readmore.${object?.course_type === 1 ? "knowledge_" : ""}stats.cost_${!!object?.mollie_data?.cost && parseFloat(object?.mollie_data?.cost) > 0 ? 'paying' : 'free'}`, {cost: object?.mollie_data?.cost})}`} />
              </ListItem>
              {parseInt(object?.mollie_data?.immediate_payment?.toString() || '0', 10) === 1 && <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{
                    backgroundColor: 'white',
                    border: '1px solid transparent',
                    borderColor: theme.palette.primary.main,
                    "& img": {
                      maxHeight: '100%',
                      maxWidth: '100%',
                      width: 'auto',
                      height: 'auto'
                    }
                  }}>
                    <PointOfSale color="primary" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={`${t(`client.course_readmore.stats.immediate_payment_required`)}`} />
              </ListItem>}
            </List>
            <Divider />
            <Box sx={{
              padding: 1,
              position: 'relative',
              height: '56px'
            }}>
              {(object?.access_data?.availability || [1]).map((lang_id, i) => <Avatar sx={{
                position: 'absolute',
                top: 8,
                left: (16 + (i * 30)),
                backgroundColor: theme.palette.primary.main,
                border: '1px solid white',
                zIndex: 1201 - i
              }}>
                {HelpersLanguage.mapping_to_iso(lang_id)}
              </Avatar>)}
              
            </Box>
          </CardContent>
        </CardBase>
      </Grid>}








      {!!registerLogin && !showSuccess && <Grid item xs={12} md={8}>
        <CardBase>
          <CardContent>
            <Typography sx={{
              color: theme.palette.tertiary?.main,
              marginBottom: 1
            }}>{t("client.appointment.step.account", "Aanmelden of registreren")}</Typography>
            {!validatedUser && <>
              <Box sx={{
                width: '100%'
              }}>
                <Grid container spacing="8">
                  <Grid item xs={12} md={4}>
                    <Box sx={{
                      width: '100%',
                      height: '100px',
                      borderRadius: '16px 16px 0px 0px',
                      // backgroundImage: `url(${ImgLogin})`,
                      // backgroundSize: 'cover',
                      // backgroundPosition: 'center center',
                      cursor: 'pointer',
                      borderLeft: '1px solid transparent',
                      borderLeftColor: theme.palette.primary.main,
                      borderRight: '1px solid transparent',
                      borderRightColor: theme.palette.primary.main,
                      borderTop: '1px solid transparent',
                      borderTopColor: theme.palette.primary.main,
                      opacity: !hasChosen ? 1 : (!!showLogin ? 1 : 0.6),
                      backgroundColor: theme.palette.primary.main,
                      display: 'table',
                      textAlign: 'center'
                    }} onClick={() => {
                      setShowLogin(true);
                      setIsGuest(false);
                      setIsRestricted(false);
                      setHasChosen(true);
                    }}>
                      <Box sx={{
                        display: 'table-cell',
                        verticalAlign: 'middle'
                      }}>
                        <Person sx={{
                          color: 'white',
                          width: '70px',
                          height: '70px'
                        }} />
                      </Box>
                    </Box>
                    <Box sx={{
                      width: '100%',
                      height: '100px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      padding: 1,
                      borderLeft: '1px solid transparent',
                      borderLeftColor: theme.palette.primary.main,
                      borderRight: '1px solid transparent',
                      borderRightColor: theme.palette.primary.main,
                      opacity: !hasChosen ? 1 : (!!showLogin ? 1 : 0.6),
                      display: 'table'
                    }} onClick={() => {
                      setShowLogin(true);
                      setIsGuest(false);
                      setIsRestricted(false);
                      setHasChosen(true);
                    }}>
                      <Typography sx={{display: 'table-cell', verticalAlign: 'middle'}}>{t("client.appointment.step.account_cta.existing")}</Typography>
                    </Box>
                    <Button
                      label={t("public.btn.choose")}
                      id="switch_both"
                      contained
                      fullwidth
                      onClick={() => {
                        setShowLogin(true);
                        setIsGuest(false);
                        setIsRestricted(false);
                        setHasChosen(true);
                      }}
                      sx={{
                        marginTop: 0,
                        borderRadius: '0px 0px 16px 16px',
                        textTransform: 'none !important',
                        opacity: !hasChosen ? 1 : (!!showLogin ? 1 : 0.6),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{
                      width: '100%',
                      height: '100px',
                      borderRadius: '16px 16px 0px 0px',
                      // backgroundImage: `url(${ImgAcc})`,
                      // backgroundSize: 'cover',
                      // backgroundPosition: 'center center',
                      cursor: 'pointer',
                      borderLeft: '1px solid transparent',
                      borderLeftColor: theme.palette.primary.main,
                      borderRight: '1px solid transparent',
                      borderRightColor: theme.palette.primary.main,
                      borderTop: '1px solid transparent',
                      borderTopColor: theme.palette.primary.main,
                      opacity: !hasChosen ? 1 : (!!showLogin ? 0.6 : (!isRestricted ? 1 : 0.6)),
                      backgroundColor: theme.palette.primary.main,
                      display: 'table',
                      textAlign: 'center'
                    }} onClick={() => {
                      setShowLogin(false);
                      setIsGuest(false);
                      setIsRestricted(false);
                      setHasChosen(true);
                    }}>
                      <Box sx={{
                        display: 'table-cell',
                        verticalAlign: 'middle'
                      }}>
                        <PersonAddAlt1 sx={{
                          color: 'white',
                          width: '70px',
                          height: '70px'
                        }} />
                      </Box>
                    </Box>
                    <Box sx={{
                      width: '100%',
                      height: '100px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      padding: 1,
                      borderLeft: '1px solid transparent',
                      borderLeftColor: theme.palette.primary.main,
                      borderRight: '1px solid transparent',
                      borderRightColor: theme.palette.primary.main,
                      opacity: !hasChosen ? 1 : (!!showLogin ? 0.6 : (!isRestricted ? 1 : 0.6)),
                      display: 'table'
                    }} onClick={() => {
                      setShowLogin(false);
                      setIsGuest(false);
                      setIsRestricted(false);
                      setHasChosen(true);
                    }}>
                      <Typography sx={{display: 'table-cell', verticalAlign: 'middle'}}>{t("client.appointment.step.account_cta.new_for_knowledge")}</Typography>
                    </Box>
                    <Button
                      label={t("public.btn.choose")}
                      id="switch_both"
                      contained
                      fullwidth
                      onClick={() => {
                        setShowLogin(false);
                        setIsGuest(false);
                        setIsRestricted(false);
                        setHasChosen(true);
                      }}
                      sx={{
                        marginTop: 0,
                        borderRadius: '0px 0px 16px 16px',
                        textTransform: 'none !important',
                        opacity: !hasChosen ? 1 : (!!showLogin ? 0.6 : (!isRestricted ? 1 : 0.6)),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{
                      width: '100%',
                      height: '100px',
                      borderRadius: '16px 16px 0px 0px',
                      // backgroundImage: `url(${ImgAcc})`,
                      // backgroundSize: 'cover',
                      // backgroundPosition: 'center center',
                      cursor: 'pointer',
                      borderLeft: '1px solid transparent',
                      borderLeftColor: theme.palette.primary.main,
                      borderRight: '1px solid transparent',
                      borderRightColor: theme.palette.primary.main,
                      borderTop: '1px solid transparent',
                      borderTopColor: theme.palette.primary.main,
                      opacity: !hasChosen ? 1 : (!!showLogin ? 0.6 : (!!isRestricted ? 1 : 0.6)),
                      backgroundColor: theme.palette.primary.main,
                      display: 'table',
                      textAlign: 'center'
                    }} onClick={() => {
                      setShowLogin(false);
                      setIsGuest(false);
                      setIsRestricted(true);
                      setHasChosen(true);
                    }}>
                      <Box sx={{
                        display: 'table-cell',
                        verticalAlign: 'middle'
                      }}>
                        <AddBusinessIcon sx={{
                          color: 'white',
                          width: '70px',
                          height: '70px'
                        }} />
                      </Box>
                    </Box>
                    <Box sx={{
                      width: '100%',
                      height: '100px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      padding: 1,
                      borderLeft: '1px solid transparent',
                      borderLeftColor: theme.palette.primary.main,
                      borderRight: '1px solid transparent',
                      borderRightColor: theme.palette.primary.main,
                      opacity: !hasChosen ? 1 : (!!showLogin ? 0.6 : (!!isRestricted ? 1 : 0.6)),
                      display: 'table'
                    }} onClick={() => {
                      setShowLogin(false);
                      setIsGuest(false);
                      setIsRestricted(true);
                      setHasChosen(true);
                    }}>
                      <Typography sx={{display: 'table-cell', verticalAlign: 'middle'}}>{t("client.appointment.step.account_cta.new_restricted_for_knowledge")}</Typography>
                    </Box>
                    <Button
                      label={t("public.btn.choose")}
                      id="switch_both"
                      contained
                      fullwidth
                      onClick={() => {
                        setShowLogin(false);
                        setIsGuest(false);
                        setIsRestricted(true);
                        setHasChosen(true);
                      }}
                      sx={{
                        marginTop: 0,
                        borderRadius: '0px 0px 16px 16px',
                        textTransform: 'none !important',
                        opacity: !hasChosen ? 1 : (!!showLogin ? 0.6 : (!!isRestricted ? 1 : 0.6)),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </>}

            {!!hasChosen && !!showLogin && <Grid container spacing={1}>
              {mutationLogin.isPending && <Grid item xs={12} sx={{
                textAlign: "center",
                height: 150
              }}>
                <CircularProgress sx={{margin: 2}} />
              </Grid>}
              {!mutationLogin.isPending && <Grid item xs={0} sm={2} md={3} lg={4} xl={5}></Grid>}
              {!mutationLogin.isPending && !!validatedUser && <>
                <Grid item container spacing={1} xs={12} sm={8} md={6} lg={4} xl={2}>
                  <Grid item xs={12}>
                    <Box sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      display: "flex",
                      flex: 1
                    }}>
                      <Avatar sx={{
                        height: 120,
                        width: 120
                      }} src={validatedUser.get_medium}></Avatar>
                      <Typography variant="subtitle1" sx={{
                        marginTop: 1.5,
                        fontSize: "1.3rem",
                        fontWeight: 'bold'
                      }}>{validatedUser.full_name}</Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Grid item container spacing={1} xs={12}>
                  {!!currentPractice && (validatedUser.known_practice_list || []).indexOf(currentPractice?.id || 0) >= 0 && <>
                    <Grid item xs={12}>
                      <Typography>{t("general.onboarding.warnings.known_practice_through_making_appointment")}</Typography>
                    </Grid>
                  </>}
                  {!!currentPractice && (validatedUser.known_practice_list || []).indexOf(currentPractice?.id || 0) < 0 && <>
                    <Grid item xs={12}>
                      <Typography>{t("general.onboarding.warnings.accept_gdpr_practice_through_making_appointment")}</Typography>
                    </Grid>
                    <Grid item xs={12} sx={{display: "flex"}}>
                      <Button
                        contained
                        id="toggle_gdpr"
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPracticeGdpr(!showPracticeGdpr);
                        }}
                        label={t(showPracticeGdpr ? "client.register.gdpr_practice_toggle_off" : "client.register.gdpr_practice_toggle_on")}
                      />
                    </Grid>
                    {!!showPracticeGdpr && <Grid item xs={12} sx={{display: "flex"}}>
                      <Typography html>{currentPractice.get_description}</Typography>
                    </Grid>}
                  </>}
                </Grid>
              </>}
              {!mutationLogin.isPending && !validatedUser && <Grid item container spacing={1} xs={12} md={3} lg={4} xl={5}>
                <Grid item xs={12}>
                  <TextInput
                    error={!!isLoginError ? t("email_error") : undefined}
                    muiIcon={<Email
                      sx={{
                        color: "rgb(195,198,197)"
                      }}
                    />}
                    id="email"
                    placeholder={t("shared.signin.field.email")}
                    autoComplete="email"
                    autoFocus={false}
                    value={email}
                    onChange={(v) => {setEmail(v);}}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    error={!!isLoginError ? t("password_error") : undefined}
                    icon={"fa-key"}
                    id="password"
                    placeholder={t("shared.signin.field.password")}
                    type="password"
                    autoComplete="current-password"
                    autoFocus={false}
                    value={password}
                    onChange={(v) => {setPassword(v);}}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    label={t("shared.buttons.sign_in")}
                    id='login'
                    contained
                    disabled={!email || !password || !(!!email && email.indexOf('@') > -1 && email.indexOf('.') > -1)}
                    fullwidth
                    onClick={(v) => {
                      mutationLogin.mutate({
                        formData: {
                          fields: ['id', 'full_name', 'get_medium', 'known_practice_list'],
                          email: email,
                          password: password
                        }
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    label={t("shared.buttons.forgot_password")}
                    id='forgot_pass'
                    text
                    fullwidth
                    onClick={(v) => {
                      setForgotPasswordOpen(true);
                    }}
                  />
                </Grid>
              </Grid>}
              {!mutationLogin.isPending && <Grid item xs={0} sm={2} md={3} lg={4} xl={5}></Grid>}
            </Grid>}
            {!!hasChosen && !showLogin && <Box>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    error={!!errors.first_name ? t("shared.signup.error.first_name") : undefined}
                    muiIcon={<Person
                      sx={{
                        color: "rgb(195,198,197)"
                      }}
                    />}
                    id="first_name"
                    caption={t("shared.signup.field.first_name")}
                    placeholder={t("shared.signup.field.first_name")}
                    autoComplete="fname"
                    autoFocus={false}
                    required
                    value={firstName || ''}
                    onChange={(v) => {
                      setFirstName(v);
                      setErrors({
                        ...errors,
                        first_name: false
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextInput
                    error={!!errors.last_name ? t("shared.signup.error.last_name") : undefined}
                    muiIcon={<Person
                      sx={{
                        color: "rgb(195,198,197)"
                      }}
                    />}
                    id="last_name"
                    caption={t("shared.signup.field.last_name")}
                    placeholder={t("shared.signup.field.last_name")}
                    autoComplete="lname"
                    required
                    value={lastName}
                    onChange={(v) => {
                      setLastName(v);
                      setErrors({
                        ...errors,
                        last_name: false
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={!!emailExists ? 8 : 12}>
                  <TextInput
                    muiIcon={<Email
                      sx={{
                        color: "rgb(195,198,197)"
                      }}
                    />}
                    error={emailExists ? t("shared.signup.error.email_exist") : (!!errors.email ? t("shared.signup.error.email") : undefined)}
                    id="email"
                    caption={t("shared.signup.field.email")}
                    placeholder={t("shared.signup.field.email")}
                    autoComplete="email"
                    type="email"
                    required
                    value={email}
                    onChange={(v) => {
                      setEmail(v);
                      setErrors({
                        ...errors,
                        email: false
                      });
                    }}
                  />
                </Grid>
                {!!emailExists && <Grid item xs={4}>
                  <Button
                    label={t("shared.buttons.request_password")}
                    id='forgot_password'
                    sx={{
                      height: '49px',
                      borderRadius: '50px'
                    }}
                    contained
                    fullwidth
                    onClick={(v) => {
                      navigate(`/login?fp=${email}`);
                      // @ts-ignore
                      if(!!document.getElementById("subscroller")) document.getElementById("subscroller").scrollTo(0, 0);
                    }}
                  />
                </Grid>}
                <Grid item xs={12}>
                  <Typography variant="caption">{t("shared.signup.field.birth_date")}</Typography>
                  <DatePicker
                    label={t("shared.signup.field.birth_date")}
                    error={!!errors.birth_date ? t("shared.signup.error.birth_date") : undefined}
                    disableFuture
                    openTo="year"
                    format="DD"
                    views={["year", "month", "day"]}
                    id="birth_day"
                    value={birthDate}
                    setValue={(v) => {
                      setBirthDate(v);
                      setErrors({
                        ...errors,
                        birth_date: false
                      });
                    }}
                    sx={{field: {marginTop: 0}}}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption">{t("shared.signup.field.country")}</Typography>
                  <Select
                    fullWidth
                    displayEmpty
                    id="country"
                    icon={<Flag sx={{
                      color: "rgb(195,198,197)",
                      marginRight: 1,
                      zIndex: 900
                    }} />}
                    error={!!errors.country ? t("shared.signup.error.country") : undefined}
                    value={country}
                    setValue={(v) => {
                      setCountry(v.toString());
                      setErrors({
                        ...errors,
                        country: false
                      });
                    }}
                  >
                    <MenuItem value="">{t("shared.signup.field.country")}</MenuItem>
                    {(countries || []).map((country) => (<MenuItem value={country.iso}>{country.name}</MenuItem>))}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    muiIcon={<PhoneAndroid
                      sx={{
                        color: "rgb(195,198,197)"
                      }}
                    />}
                    error={!!errors.phone_mobile ? t("shared.signup.error.phone_mobile") : undefined}
                    id="phone_mobile"
                    caption={t("shared.signup.field.phone_mobile")}
                    placeholder={t("shared.signup.field.phone_mobile")}
                    autoComplete="phone_mobile"
                    type="tel"
                    required
                    value={mobile}
                    onChange={(v) => {
                      setMobile(v);
                      setErrors({
                        ...errors,
                        phone_mobile: false
                      });
                    }}
                  />
                </Grid>
                {!isGuest && <Grid item xs={12} sm={6}>
                  <TextInput
                    helperText={t("shared.signup.field.password_tip")}
                    error={!!errors.password ? t("shared.signup.error.password") : undefined}
                    icon={"fa-key"}
                    id="password"
                    caption={t("shared.signup.field.password")}
                    placeholder={t("shared.signup.field.password")}
                    type="password"
                    autoComplete="new-password"
                    autoFocus={false}
                    value={password}
                    onChange={(v) => {
                      setPassword(v);
                      setErrors({
                        ...errors,
                        phone_mobile: false
                      });
                    }}
                  />
                </Grid>}
                {!isGuest && <Grid item xs={12} sm={6}>
                  <TextInput
                    error={!!errors.password_repeat ? t("shared.signup.error.password_repeat") : undefined}
                    icon={"fa-key"}
                    id="password_repeat"
                    caption={t("shared.signup.field.password_repeat")}
                    placeholder={t("shared.signup.field.password_repeat")}
                    type="password"
                    autoComplete="repeat-password"
                    autoFocus={false}
                    value={passwordRepeat}
                    onChange={(v) => {
                      setPasswordRepeat(v);
                      setErrors({
                        ...errors,
                        phone_mobile: false
                      });
                    }}
                  />
                </Grid>}

                {!!isRestricted && <>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{display:'block'}}>{t("coach.submenu.account_settings.skills")}:</Typography>
                    {!showAllChips && (coachSkills || []).sort((a, b) => (skillIds.filter(fff => fff === b.id).length - skillIds.filter(fff => fff === a.id).length) || a.name.localeCompare(b.name)).slice(0, skillIds.length > 6 ? skillIds.length : 6).map(skill => <Chip
                      sx={{marginRight: 1, marginBottom: 1, cursor: 'pointer'}}
                      label={skill.name}
                      onClick={(e) => {
                        if (skillIds.filter(fff => fff === skill.id).length > 0) {
                          setSkillIds(skillIds.filter(fff => fff !== skill.id));
                        } else {
                          setSkillIds([...skillIds, skill.id]);
                        }
                      }}
                      variant="outlined"
                      icon={skillIds.filter(fff => fff === skill.id).length > 0 ? <Done /> : <Add />}
                      color={skillIds.filter(fff => fff === skill.id).length > 0 ? "primary" : 'default'}
                    />)}
                    {!showAllChips && <Chip
                      sx={{marginRight: 1, marginBottom: 1, cursor: 'pointer'}}
                      label={t("shared.chips.show_more")}
                      onClick={(e) => {
                        setShowAllChips(true);
                      }}
                      variant="outlined"
                      icon={<Visibility />}
                      color="primary"
                    />}
                    {!!showAllChips && (coachSkills || []).sort((a, b) => (skillIds.filter(fff => fff === b.id).length - skillIds.filter(fff => fff === a.id).length) || a.name.localeCompare(b.name)).map(skill => <Chip
                      sx={{marginRight: 1, marginBottom: 1, cursor: 'pointer'}}
                      label={skill.name}
                      onClick={(e) => {
                        if (skillIds.filter(fff => fff === skill.id).length > 0) {
                          setSkillIds(skillIds.filter(fff => fff !== skill.id));
                        } else {
                          setSkillIds([...skillIds, skill.id]);
                        }
                      }}
                      variant="outlined"
                      icon={skillIds.filter(fff => fff === skill.id).length > 0 ? <Done /> : <Add />}
                      color={skillIds.filter(fff => fff === skill.id).length > 0 ? "primary" : 'default'}
                    />)}
                    {!!showAllChips && <Chip
                      sx={{marginRight: 1, marginBottom: 1, cursor: 'pointer'}}
                      label={t("shared.chips.show_less")}
                      onClick={(e) => {
                        setShowAllChips(false);
                      }}
                      variant="outlined"
                      icon={<VisibilityOff />}
                      color="primary"
                    />}
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      id="practice_name"
                      error={!!errors.practice_name ? t("shared.signup.error.practice_name") : undefined}
                      placeholder={t("shared.signup.field.practice_name")}
                      caption={t("shared.signup.field.practice_name")}
                      starred
                      value={practiceName}
                      onChange={(v) => {
                        setPracticeName(v);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      id="support_email"
                      error={!!errors.support_email ? t("shared.signup.error.support_email") : undefined}
                      placeholder={t("shared.signup.field.practice_suport_email")}
                      caption={t("shared.signup.field.practice_suport_email")}
                      starred
                      value={practiceEmail}
                      onChange={(v) => {
                        setPracticeEmail(v);
                      }}
                    />
                  </Grid>
                </>}


                <Grid item xs={12} sx={{
                  display: "flex",
                  alignItems: "center",
                  "& > label": {
                    padding: 1
                  },
                  "&.error": {
                    border: "0.5px solid rgba(255, 0, 0, 0.5)"
                  },
                  marginTop: 1
                }}>
                  <Checkbox
                    checked={gdprAccepted}
                    onChange={(e) => {
                      setGdprAccepted(e.target.checked);
                      setErrors({
                        ...errors,
                        gdpr: false
                      });
                    }}
                    value="gdpr"
                  />
                  {!errors.gdpr && <Typography html>{t(!!isRestricted ? "restricted.profile.gdpr_nutrilink_checkbox_text_short" : "client.register.gdpr_nutrilink")}</Typography>}
                  {!!errors.gdpr && <Typography html sx={{color: theme.palette.secondary.main}}>{t(!!isRestricted ? "restricted.profile.gdpr_nutrilink_checkbox_text_short" : "client.register.gdpr_nutrilink")}</Typography>}
                </Grid>
                {!!currentPractice?.get_description && !isRestricted && <>
                  <Grid item xs={12} sx={{
                    alignItems: "center",
                    display: "flex",
                    "& > label": {
                      padding: 1
                    },
                    "&.error": {
                      border: "0.5px solid rgba(255, 0, 0, 0.5)"
                    }
                  }}>
                    <Checkbox
                      checked={practiceGdprAccepted}
                      onChange={(e) => {
                        setPracticeGdprAccepted(e.target.checked);
                        setErrors({
                          ...errors,
                          gdpr_practice: false
                        });
                      }}
                      value="gdpr"
                    />
                    {!errors.gdpr_practice && <Typography html>{t("client.register.gdpr_practice").replace("{practice_name}", currentPractice.name)}</Typography>}
                    {!!errors.gdpr_practice && <Typography html sx={{color: theme.palette.secondary.main}}>{t("client.register.gdpr_practice").replace("{practice_name}", currentPractice.name)}</Typography>}
                  </Grid>
                  <Grid item xs={12} sx={{display: "flex"}}>
                    <Button
                      contained
                      id="toggle_gdpr"
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPracticeGdpr(!showPracticeGdpr);
                      }}
                      label={t(showPracticeGdpr ? "client.register.gdpr_practice_toggle_off" : "client.register.gdpr_practice_toggle_on")}
                    />
                  </Grid>
                  {!!showPracticeGdpr && <Grid item xs={12} sx={{display: "flex"}}>
                    <Typography html>{currentPractice.get_description}</Typography>
                  </Grid>}
                </>}

                <Grid item xs={12} sx={{display: {
                  xs: 'none',
                  md: "flex"
                }}}>
                  <Button
                    label={t("client.course_readmore.cta_subscribe")}
                    id="sub_course2"
                    contained
                    disabled={!validUser}
                    loading={mutationCustomAction.isPending}
                    onClick={() => {
                      mutationCustomAction.mutate({
                        formData: {
                          object: 'knowledge_course',
                          class_action: 'custom_api_subscribe',
                          id: object?.id,
                          is_restricted: isRestricted ? '1': '0',
                          new_user: {
                            first_name: firstName,
                            last_name: lastName,
                            email: email,
                            birth_date: birthDate,
                            country_id: country,
                            phone_mobile: mobile,
                            password: password,
                            is_agreed: gdprAccepted,
                            is_agreed_practice: practiceGdprAccepted
                          },
                          coach_skill_ids: skillIds,
                          practice: {
                            name: practiceName,
                            support_email: practiceEmail
                          },
                          handler_id: validatedUser?.id
                        }
                      });
                    }}
                    sx={{
                      marginTop: 1,
                      width: 'auto'
                    }}
                    fullwidth
                  />
                </Grid>

              </Grid>
            </Box>}
          </CardContent>
        </CardBase>
      </Grid>}



      {!!registerLogin && !showSuccess && <Grid item xs={12} md={4}>
        <CardBase sx={{height: 'auto'}}>
          <CardContent sx={{paddingBottom: "16px !important"}}>
            {!!showLogin && !!hasChosen && <Button
              label={t("client.course_readmore.cta_subscribe")}
              id="sub_course1"
              disabled={!validatedUser}
              contained
              onClick={() => {
                mutationCustomAction.mutate({
                  formData: {
                    object: 'knowledge_course',
                    class_action: 'custom_api_subscribe',
                    id: object?.id,
                    user_id: validatedUser?.id,
                    handler_id: validatedUser?.id
                  }
                });
              }}
              sx={{
                marginTop: 0
              }}
              fullwidth
            />}

            {!showLogin && !!hasChosen && <Button
              label={t("client.course_readmore.cta_subscribe")}
              id="sub_course2"
              disabled={!validUser}
              contained
              onClick={() => {
                mutationCustomAction.mutate({
                  formData: {
                    object: 'knowledge_course',
                    class_action: 'custom_api_subscribe',
                    id: object?.id,
                    is_restricted: isRestricted ? '1': '0',
                    new_user: {
                      first_name: firstName,
                      last_name: lastName,
                      email: email,
                      birth_date: birthDate,
                      country_id: country,
                      phone_mobile: mobile,
                      password: password
                    },
                    coach_skill_ids: skillIds,
                    practice: {
                      name: practiceName,
                      support_email: practiceEmail
                    },
                    handler_id: validatedUser?.id
                  }
                });
              }}
              sx={{
                marginTop: 0
              }}
              fullwidth
            />}

            {!object?.knowsub?.id && <Button
              label={t("client.course_readmore.cta_back")}
              id="sub_courses"
              contained={!(!!hasChosen)}
              text={!!(!!hasChosen)}
              onClick={() => {
                setRegisterLogin(false);
              }}
              sx={{
                marginTop: !!(!!hasChosen) ? 1 : 0
              }}
              fullwidth
            />}

          </CardContent>
        </CardBase>
      </Grid>}



      {!!showSuccess && <Grid item xs={12} md={8}>
        <Typography html>{t("client.course_betweenpage.generic_part")}</Typography>
        {!!object?.is_approvable && <Typography sx={{marginTop: 2}} html>{t("client.course_betweenpage.approval_flow_part")}</Typography>}
        {!!isRestricted && <Typography sx={{marginTop: 2}} html>{t("client.course_betweenpage.restricted_flow_part")}</Typography>}
        {parseInt(object?.mollie_data?.immediate_payment?.toString() || '0', 10) === 1 && <Typography sx={{marginTop: 2}} html>{t("client.course_betweenpage.payment_part_frontpage", {link: object?.knowsub?.mollie_link?.extra_data?.mollie?.payment_link})}</Typography>}

      </Grid>}
      {!!showSuccess && <Grid item xs={12} md={4}>
        <Button
          label={t("client.course_readmore.cta_back")}
          id="sub_courses"
          contained
          onClick={() => {
            navigate("/login");
          }}
          sx={{
            marginTop: 0
          }}
          fullwidth
        />
      </Grid>}
    </Grid>

    <DialogBase
      name="fp"
      loading={forgotPasswordLoading}
      title={t("shared.forgot_password.info.title")}
      content={<>
        {!forgotPasswordSuccess && <>
          <DialogContentText id="fp-dialog-description">
            <Typography>{t("shared.forgot_password.info.text")}</Typography>
          </DialogContentText>
          <TextInput
            id="fp_email"
            placeholder={t("shared.signin.field.email")}
            autoComplete="email"
            autoFocus
            margin="dense"
            value={email}
            onChange={(v) => {
              setEmail(v);
            }}
          />
          {!!email && !(email && email.indexOf('@') > -1 && email.indexOf('.') > -1) && <Typography sx={{
            color: theme.palette.warning.main,
            fontStyle: 'italic'
          }}>{t('shared.forgot_password.error.invalid_email')}</Typography>}
        </>}
        {!!forgotPasswordSuccess && <DialogContentText id="fp-dialog-description">
          <Typography>{t("shared.forgot_password.success.text")}</Typography>
        </DialogContentText>}
      </>}
      actions={<>
        {!forgotPasswordSuccess && <DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
          <Btn onClick={() => {setForgotPasswordOpen(false);}}>{t("shared.buttons.cancel")}</Btn>
          <Btn variant="contained" onClick={() => {
            mutationForgotPassword.mutate({
              formData: {
                email: email,
                current_practice: currentPractice?.id
              }
            });
          }}>{t("shared.buttons.confirm")}</Btn>
        </DialogActions>}
        {!!forgotPasswordSuccess && <DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
          <Btn onClick={() => {setForgotPasswordOpen(false);}}>{t("shared.buttons.close")}</Btn>
        </DialogActions>}
      </>}
      open={forgotPasswordOpen}
      setOpen={setForgotPasswordOpen}
    />
  </Page>;
}
